import React, { type ReactNode } from "react"
import {
  Box,
  Flex,
  Image,
  Text,
} from "@chakra-ui/react"
import QRCodePlaceholder from "components/elements/QRCodePlaceholder"
import { PassAuxiliaryField } from "./walletPassBuilderTypes"

export type AppleWalletPassPreviewProps = {
  brandName: string
  timeText: string
  dateText: string
  eventName: string
  locationName: string
  /** Primary, when a banner pushed it into the first secondary slot. */
  leadSecondaryField?: { label?: string; value: string }
  auxiliaryFields?: PassAuxiliaryField[]
  /** @deprecated Use `labelColor` */
  accentColor?: string
  backgroundColor?: string
  textColor?: string
  labelColor?: string
  logoImageUrl?: string
  logoIsHorizontal?: boolean
  heroImageUrl?: string
  qrImageUrl?: string
  showQr?: boolean
  primaryLabel?: string
  secondaryLabel?: string
}

// Fallbacks match the API's WalletPass::Customization::DEFAULTS.
const DEFAULT_BACKGROUND = "#EBECF1"
const DEFAULT_TEXT = "#161616"
const DEFAULT_LABEL = "#3A7AD8"
const PREVIEW_WIDTH = 320
/** Height the content is laid out against — drives the vertical ref→px scale. */
const CONTENT_HEIGHT = 509
/** Actual rendered card height. Extra height past CONTENT_HEIGHT just grows the
 *  card (and pushes the bottom-anchored QR down) without stretching the content. */
const PREVIEW_HEIGHT = CONTENT_HEIGHT + 36

/** Reference artboard: `apple-passbook.svg` pass card (375×551, Apple spec width = 375pt) */
const REF_WIDTH = 375
const REF_HEIGHT = 551
const scaleX = PREVIEW_WIDTH / REF_WIDTH
const scaleY = CONTENT_HEIGHT / REF_HEIGHT
const px = (value: number) => `${Math.round(value * scaleX)}px`
const py = (value: number) => `${Math.round(value * scaleY)}px`

const FONT_FAMILY = "system-ui, -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', sans-serif"

/** Horizontal inset used for most pass content (matches SVG field `x=16`). */
const CONTENT_PAD_X = 16

/** SVG `tspan` y is the text baseline; shift up by cap height for CSS `top`. */
const baselineTop = (y: number, fontSize: number) => py(y - fontSize)

type PassTextProps = {
  y: number
  fontSize: number
  color: string
  fontWeight?: number
  /** Anchor to the left or right card edge using `pl` / `pr` insets. */
  textAlign?: "left" | "right"
  /** Inset from the left edge (ref coords). Used when `textAlign` is `left`, or to cap width. */
  pl?: number
  /** Inset from the right edge (ref coords). Used when `textAlign` is `right`, or to cap width. */
  pr?: number
  noOfLines?: number
  isTruncated?: boolean
  whiteSpace?: "nowrap"
  uppercase?: boolean
  children: ReactNode
}

const PassText = ({
  y,
  fontSize,
  color,
  fontWeight = 400,
  textAlign = "left",
  pl = CONTENT_PAD_X,
  pr,
  noOfLines,
  isTruncated,
  whiteSpace,
  uppercase,
  children,
}: PassTextProps) => {
  const anchoredRight = textAlign === "right"
  const rightInset = pr ?? CONTENT_PAD_X
  const leftInset = pl ?? CONTENT_PAD_X

  return (
    <Text
      position="absolute"
      left={anchoredRight ? undefined : px(leftInset)}
      right={anchoredRight ? px(rightInset) : pr !== undefined ? px(pr) : undefined}
      top={baselineTop(y, fontSize)}
      fontSize={px(fontSize)}
      lineHeight="1.2"
      fontWeight={fontWeight}
      color={color}
      fontFamily={FONT_FAMILY}
      textAlign={textAlign}
      textTransform={uppercase ? "uppercase" : undefined}
      noOfLines={noOfLines}
      isTruncated={isTruncated}
      whiteSpace={whiteSpace}
    >
      {children}
    </Text>
  )
}

type FieldLabelProps = {
  color: string
  children: ReactNode
}

/** Small-caps field label used in the normal-flow body stack. */
const FieldLabel = ({ color, children }: FieldLabelProps) => (
  <Text
    fontSize={px(LABEL_SIZE)}
    lineHeight="1"
    fontWeight={600}
    color={color}
    fontFamily={FONT_FAMILY}
    textTransform="uppercase"
    whiteSpace="nowrap"
    isTruncated
  >
    {children}
  </Text>
)

type FieldValueProps = {
  color: string
  size?: number
  noOfLines?: number
  children: ReactNode
}

/** Roomier line-height so wrapped values breathe. The added top/bottom leading is
 *  trimmed with negative margins (= half the extra leading, in em) so the box keeps
 *  the same outer footprint as line-height 1 — effective field spacing is unchanged. */
const FIELD_VALUE_LINE_HEIGHT = 1.3
const FIELD_VALUE_LEADING_TRIM = `-${(FIELD_VALUE_LINE_HEIGHT - 1) / 2}em`

/** Field value used in the normal-flow body stack. Single-line values truncate. */
const FieldValue = ({
  color,
  size = FIELD_VALUE_SIZE,
  noOfLines = 1,
  children,
}: FieldValueProps) => (
  <Text
    fontSize={px(size)}
    lineHeight={FIELD_VALUE_LINE_HEIGHT}
    mt={FIELD_VALUE_LEADING_TRIM}
    mb={FIELD_VALUE_LEADING_TRIM}
    color={color}
    fontFamily={FONT_FAMILY}
    noOfLines={noOfLines}
    isTruncated={noOfLines === 1}
  >
    {children}
  </Text>
)

// Header
const LOGO_X = 12
// PkpassAssets writes the square upload into the logo.png slot at max 160×50,
// so a square logo renders 50×50 pt on the real pass — mirror that here.
const LOGO_Y = 8
const LOGO_SIZE = 50
// Apple Wallet logo spec: 160×50 pt at @1x (REF coords ≈ pt)
const LOGO_H_WIDTH = 160 // 160pt per Apple spec
const LOGO_H_HEIGHT = 50 // 50pt per Apple spec
const LOGO_H_Y = 8 // vertically centred in the ~67pt header: (67-50)/2 ≈ 8
const BRAND_X = 68 // 12 (left pad) + 50 (logo) + 6 (gap)
const BRAND_Y = 41 // vertically centred with LOGO_SIZE=50 at LOGO_Y=8: 8+50/2+15/2 ≈ 41
const BRAND_SIZE = 19
const TIME_Y = 21
const TIME_SIZE = 10
const DATE_Y = 44
const DATE_SIZE = 22

// Hero strip (masked bitmap group)
const HERO_X = 0
const HERO_Y = 67
const HERO_W = 375
const HERO_H = 98
const HERO_STRIP_HEIGHT_PX = Math.round(HERO_H * scaleY)

// Card height is independent of the banner: a banner overlays content within this
// height rather than extending the card, so enabling it never grows the pass.
export const getApplePassPreviewHeight = () => PREVIEW_HEIGHT - HERO_STRIP_HEIGHT_PX

// Primary / secondary / auxiliary fields
const LABEL_SIZE = 12
const EVENT_VALUE_SIZE = 22
const FIELD_VALUE_SIZE = 20

/** Adaptive font size: measure text width via Canvas, then scale to fill the banner. */
const BANNER_AVAILABLE_PX = (HERO_W - 2 * CONTENT_PAD_X) * (PREVIEW_WIDTH / REF_WIDTH)
const bannerValueSize = (text: string): number => {
  try {
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")
    if (!ctx) throw new Error()
    const refSize = 100
    ctx.font = `200 ${refSize}px system-ui, -apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif`
    const measured = ctx.measureText(text).width
    if (measured === 0) throw new Error()

    return Math.max(10, Math.min(72, Math.round((refSize * BANNER_AVAILABLE_PX) / measured)))
  } catch {
    return Math.max(10, Math.min(72, Math.round(680 / Math.max(text.length, 1))))
  }
}

// Body flow rhythm (ref-pt). Labels/values use tight line-heights (1 / 1.1) so a label
// sits snug above its value with no leading gap; FIELD_ROW_GAP is the larger space
// between adjacent field groups.
const FIELD_ROW_GAP = 20

// QR block — square (width-based) so the QR has no extra vertical padding
const QR_X = 105
const QR_SIZE = 165
const QR_RADIUS = 5
const QR_INSET_X = 4
const QR_INNER_SIZE = 157

const AppleWalletPassPreview = ({
  brandName,
  timeText,
  dateText,
  eventName,
  locationName,
  leadSecondaryField,
  auxiliaryFields = [],
  accentColor,
  backgroundColor = DEFAULT_BACKGROUND,
  textColor = DEFAULT_TEXT,
  labelColor: labelColorProp,
  logoImageUrl,
  logoIsHorizontal,
  heroImageUrl,
  qrImageUrl,
  showQr = true,
  primaryLabel = "Event",
  secondaryLabel = "Location",
}: AppleWalletPassPreviewProps) => {
  const labelColor = labelColorProp ?? accentColor ?? DEFAULT_LABEL
  const previewHeight = getApplePassPreviewHeight()

  // Card outline bottom edge tracks the (possibly shortened) preview height so the
  // bottom border stays inside the `overflow:hidden` viewport. Inset by 0.424819 to
  // mirror the top inset and keep the 1px stroke fully on-canvas.
  const cardBottom = previewHeight - 0.424819

  return (
    <Box position="relative" w={`${PREVIEW_WIDTH}px`} h={`${previewHeight}px`} overflow="hidden">
      <Box position="absolute" inset={0} pointerEvents="none" userSelect="none">
        <svg
          width={PREVIEW_WIDTH}
          height={previewHeight}
          viewBox={`0 0 ${PREVIEW_WIDTH} ${previewHeight}`}
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label="Apple Wallet pass preview"
          preserveAspectRatio="xMidYMid meet"
          style={{ display: "block" }}
        >
          <path
            d={[
              "M 186.102181,0.424702",
              "L 319.151194,0.424819",
              "L 319.575597,0.424819",
              `L 319.575597,${cardBottom}`,
              `L 0.424403,${cardBottom}`,
              "L 0.424403,0.424819",
              "L 0.848806,0.424819",
              "L 134.746625,0.424702",
              "L 134.956897,0.424702",
              "L 135.084283,0.592159",
              "C 141.068899,8.459309 150.356154,13.169384 160.424403,13.169384",
              "C 170.492652,13.169384 179.779907,8.459309 185.764523,0.592159",
              "L 185.891910,0.424702",
              "Z",
            ].join(" ")}
            stroke="#DBDBDB"
            strokeWidth="1"
            fill={backgroundColor}
            fillRule="evenodd"
          />
        </svg>
      </Box>

      {/* Header — horizontal: logo + brand in a flex row so brand follows actual logo width */}
      {logoIsHorizontal ? (
        <Flex
          position="absolute"
          left={px(LOGO_X)}
          top={py(LOGO_H_Y)}
          right={px(CONTENT_PAD_X)}
          maxH={py(LOGO_H_HEIGHT)}
          align="center"
          gap={px(6)}
        >
          {logoImageUrl && (
            <Box
              flexShrink={0}
              borderRadius="4px"
              overflow="hidden"
            >
              <Image
                src={logoImageUrl}
                alt="Pass logo"
                w="auto"
                h="auto"
                maxW={px(LOGO_H_WIDTH)}
                maxH={py(LOGO_H_HEIGHT)}
                display="block"
                draggable={false}
              />
            </Box>
          )}
          {brandName && (
            <Text
              flex={1}
              minW={0}
              fontSize={px(BRAND_SIZE)}
              lineHeight="1.2"
              fontWeight={600}
              color={textColor}
              fontFamily={FONT_FAMILY}
              isTruncated
            >
              {brandName}
            </Text>
          )}
        </Flex>
      ) : (
        <>
          {logoImageUrl && (
          <Box
            position="absolute"
            left={px(LOGO_X)}
            top={py(LOGO_Y)}
            w={px(LOGO_SIZE)}
            h={px(LOGO_SIZE)}
            overflow="hidden"
          >
            <Image
              src={logoImageUrl}
              alt="Pass logo"
              w="100%"
              h="100%"
              objectFit="contain"
              draggable={false}
            />
          </Box>
          )}

          {brandName && (
          <PassText
            y={BRAND_Y}
            fontSize={BRAND_SIZE}
            fontWeight={600}
            color={textColor}
            pl={logoImageUrl ? BRAND_X : CONTENT_PAD_X}
            pr={CONTENT_PAD_X}
            isTruncated
          >
            {brandName}
          </PassText>
          )}
        </>
      )}

      <PassText y={TIME_Y} fontSize={TIME_SIZE} fontWeight={600} color={labelColor} textAlign="right" whiteSpace="nowrap" uppercase>
        {timeText}
      </PassText>

      <PassText y={DATE_Y} fontSize={DATE_SIZE} color={textColor} textAlign="right" whiteSpace="nowrap">
        {dateText}
      </PassText>

      {/* Hero strip — only when a banner image is configured */}
      {heroImageUrl && (
      <Box
        position="absolute"
        left={px(HERO_X)}
        top={py(HERO_Y)}
        w={px(HERO_W)}
        h={py(HERO_H)}
        overflow="hidden"
      >
        <Image
          src={heroImageUrl}
          alt="Pass hero"
          w="100%"
          h="100%"
          objectFit="cover"
          draggable={false}
        />
      </Box>
      )}

      {/* Primary field value — top-anchored inside banner to match iOS layout */}
      {heroImageUrl && eventName && (
        <Text
          position="absolute"
          top={py(HERO_Y + 8)}
          left={px(CONTENT_PAD_X)}
          right={px(CONTENT_PAD_X)}
          fontSize={px(bannerValueSize(eventName))}
          lineHeight="1.2"
          fontWeight={200}
          // iPhones render the strip-overlay primary white regardless of
          // foregroundColor or banner brightness, with no shadow (verified on
          // device — fields below the banner do get the configured colors).
          color="#FFFFFF"
          fontFamily={FONT_FAMILY}
          noOfLines={1}
          isTruncated
        >
          {eventName}
        </Text>
      )}

      {/* Body fields — normal flow so a wrapping title pushes the following fields
          down instead of overlapping them. Anchored below the header (no banner) or
          below the banner strip (banner present); the bottom-anchored QR is unaffected. */}
      <Flex
        direction="column"
        position="absolute"
        left={px(CONTENT_PAD_X)}
        right={px(CONTENT_PAD_X)}
        top={heroImageUrl ? py(HERO_Y + HERO_H + 18) : py(HERO_Y + 6)}
        gap={py(FIELD_ROW_GAP)}
      >
        {!heroImageUrl && eventName && (
          <Flex direction="column">
            <FieldLabel color={labelColor}>{primaryLabel}</FieldLabel>
            <FieldValue color={textColor} size={EVENT_VALUE_SIZE} noOfLines={2}>
              {eventName}
            </FieldValue>
          </Flex>
        )}

        {(locationName || leadSecondaryField?.value) && (
          <Flex gap={px(12)}>
            {leadSecondaryField?.value && (
              <Box flex={1} minW={0}>
                <FieldLabel color={labelColor}>{leadSecondaryField.label}</FieldLabel>
                <FieldValue color={textColor}>{leadSecondaryField.value}</FieldValue>
              </Box>
            )}
            {locationName && (
              <Box flex={1} minW={0}>
                <FieldLabel color={labelColor}>{secondaryLabel}</FieldLabel>
                <FieldValue color={textColor}>{locationName}</FieldValue>
              </Box>
            )}
          </Flex>
        )}

        {auxiliaryFields.length > 0 && (
          <Flex direction="column">
            <Flex justify="space-between" gap={px(12)}>
              {auxiliaryFields.map((aux, i) => (
                <Box key={`aux-${i}`} flex="0 1 auto" minW={0}>
                  <FieldLabel color={labelColor}>
                    {aux.label}
                  </FieldLabel>
                  <FieldValue color={textColor}>
                    {aux.value}
                  </FieldValue>
                </Box>
              ))}
            </Flex>
          </Flex>
        )}
      </Flex>

      {/* QR */}
      {showQr && (
      <Box
        position="absolute"
        left={px(QR_X)}
        bottom={py(16)}
        w={px(QR_SIZE)}
        h={px(QR_SIZE)}
        borderRadius={px(QR_RADIUS)}
        bg="white"
      >
        <Box
          position="absolute"
          left={px(QR_INSET_X)}
          top={px(QR_INSET_X)}
          w={px(QR_INNER_SIZE)}
          h={px(QR_INNER_SIZE)}
          bg="gray.100"
          borderRadius={px(3)}
          overflow="hidden"
        >
          {qrImageUrl ? (
            <Image
              src={qrImageUrl}
              alt="QR code"
              w="100%"
              h="100%"
              objectFit="contain"
              draggable={false}
            />
          ) : (
            <QRCodePlaceholder size="100%" text="Diobox Pass Builder" />
          )}
        </Box>
      </Box>
      )}
    </Box>
  )
}

export default AppleWalletPassPreview
